import React, {Component, useState} from 'react';
import classNames from 'classnames/bind';
import '../../faq-sections.css';
import {faqRefusalNotice, getFAQAnalyticsBootstrapUrl, loadFAQSectionData, sectionText} from './utils';
import { trackWithBootstrap } from '../analytics/host-beacon';
import { isEmpty, map } from 'lodash';

let cx = classNames.bind({});

// Per mount, not per question: an author can embed one section twice in the
// same article, and `aria-controls` must name exactly one answer.
let answerSequence = 0;

const ArticleItem = (props) => {
    const [collapsed, setCollapsed] = useState(true);
    const [answerId] = useState(() => `hc-faq-answer-${++answerSequence}`);
    const articleIsVisible = !props.collapsedItems || !collapsed;

    React.useEffect(() => {
        if (!articleIsVisible || !props.analyticsBootstrapUrl || !props.article.id) {
            return undefined;
        }

        trackWithBootstrap(props.analyticsBootstrapUrl, 'trackArticleView', {
            faqSectionId: props.sectionId,
            articleId: props.article.id,
            articleViewType: 'faq_expand',
        }).catch(() => null);

        let secondsRead = 0;
        const intervalId = setInterval(() => {
            if (document.hidden) {
                return;
            }

            secondsRead += 5;
            trackWithBootstrap(props.analyticsBootstrapUrl, 'trackReadProgress', {
                faqSectionId: props.sectionId,
                articleId: props.article.id,
                secondsRead,
            }).catch(() => null);
        }, 5000);

        return () => clearInterval(intervalId);
    }, [articleIsVisible, props.analyticsBootstrapUrl, props.article.id, props.sectionId]);

    // The question row IS the disclosure button, so a screen reader announces
    // the question itself, "collapsed" or "expanded", and Space toggles it. It
    // used to be an icon-only `<a href="#">` beside the question, which read as
    // an unnamed link with no state (the "+" toggle in the VPAT's 1.1.1 row).
    return (
        <li className={cx("hc-faqs-article-item", props.collapsedItems ? "collapsed" : 'null')}>
            {props.collapsedItems ?
                <button type="button"
                        className="hc-faqs-article-item-title d-flex justify-content-between"
                        aria-expanded={!collapsed}
                        aria-controls={collapsed ? undefined : answerId}
                        onClick={() => setCollapsed(!collapsed)}>
                    <strong>{props.article.title}</strong>
                    <span className="hc-faqs-article-item-icon" aria-hidden="true"><i className={cx("fas", collapsed ? 'fa-plus' : 'fa-minus')} /></span>
                </button> :
                <div className="hc-faqs-article-item-title d-flex justify-content-between">
                    <strong>{props.article.title}</strong>
                </div>}
            {articleIsVisible ?
                <p id={answerId} className={cx({'hc-faqs-article-item-content': true})} dangerouslySetInnerHTML={{__html: props.article.content}} />: null}
        </li>
    );
};

export default class FAQSection extends Component {
    constructor(props) {
        super(props);

        const options = window.hcOptions || {};
        this.defaultLanguage = null;

        this.state = {
            section: {},
            articles: [],
            loading: true,
            unavailable: null,
            lang: options.lang || 'default'
        };
    }

    componentDidMount() {
        loadFAQSectionData(this.props.id, (data) => {
            if (this.unmounted) {
                return;
            }

            this.defaultLanguage = data.defaultLanguage;
            this.setState({
                section: data.section,
                articles: data.articles,
                loading: false,
                lang: this.state.lang === data.defaultLanguage ? 'default' : this.state.lang,
            });

            if (this.tracksAnalytics()) {
                trackWithBootstrap(getFAQAnalyticsBootstrapUrl(this.props.id), 'trackPageView', {
                    faqSectionId: this.props.id,
                }).catch(() => null);
            }
        }, this.props.isPreview || false, this.state.lang, (reason) => {
            // Nothing to show: render nothing, the way a section with no title
            // already does, rather than a preloader that never resolves. An IP
            // refusal is the exception: the visitor is told why.
            if (!this.unmounted) {
                this.setState({loading: false, unavailable: faqRefusalNotice(reason)});
            }
        });
    }

    componentWillUnmount() {
        // The customizer preview swaps the article out from under a mounted
        // section on every live edit and unmounts it; a read still in flight
        // must not land on the dead component.
        this.unmounted = true;
    }

    /**
     * `trackAnalytics={false}` is the customizer preview: the team member
     * editing their own draft is not a reader, and the rest of the preview
     * already sends no analytics.
     */
    tracksAnalytics() {
        return this.props.trackAnalytics !== false;
    }

    /**
     * Whether there is nothing to show this reader: the read failed, or none
     * of the section's questions is theirs to see. On a localized page that is
     * every question not yet released in the page's language — the endpoint
     * lists only those that are, as every other list on the page does. A
     * heading over an empty list, in the middle of an article, reads as a
     * broken block. The dashboard's previews (`isPreview`) keep it: there the
     * author needs to see the block exists.
     */
    hasNothingToShow() {
        return !this.state.section.title
            || (!this.props.isPreview && isEmpty(this.state.articles));
    }

    render() {
        if (this.state.loading) {
            return <div className="text-center"><i className="fas fa-spinner fa-spin"></i> FAQ section is loading...</div>;
        }

        if (this.state.unavailable) {
            return <p className="hc-faqs-unavailable" role="status">{this.state.unavailable}</p>;
        }

        if (this.hasNothingToShow()) {
            return null;
        }

        const section = this.state.section;
        const title = sectionText(section.title, this.state.lang);
        const description = sectionText(section.description, this.state.lang);

        // The design studio passes the heading choice being edited before it is saved.
        const showHeading = typeof this.props.showHeading === 'boolean'
            ? this.props.showHeading
            : (!section.options || section.options.showHeading !== false);

        return (
            <div className={'hc-faqs-' + this.props.id} style={{margin: '1rem 0'}}>
                {showHeading ?
                    <div className="hc-faqs-heading" style={{marginBottom:'3rem',textAlign:'center'}}>
                        <h2 className="hc-faqs-title" style={{fontWeight:'700'}}>{title}</h2>
                        {description ? <p className="lead">{description}</p>: null}
                    </div>: null}
                <ul className="hc-faqs-listing">
                    {map(this.state.articles, (article) => {
                        return <ArticleItem
                            key={article.id}
                            article={article}
                            collapsedItems={this.props.style === 'collapsed'}
                            analyticsBootstrapUrl={this.tracksAnalytics() ? getFAQAnalyticsBootstrapUrl(this.props.id) : null}
                            sectionId={this.props.id}
                        />;
                    })}
                </ul>
            </div>
        );
    }
}
